import { createContext, useContext } from "react";
import type * as React from "react";
import type { WidgetSettingField, WidgetSettingOption } from "./types";
import type { Omittable } from "../../lib/props";

/**
 * Props a custom settings control receives. A control owns rendering ONE field of
 * an open `kind`; it reads/writes the value through `onCommit(field.key, value)`.
 */
export type WidgetControlProps = Omittable<{
  field: WidgetSettingField;
  value: unknown;
  disabled?: boolean;
  onCommit: (key: string, value: unknown) => void;
}>;

/**
 * The extensibility seam: a consumer registers controls for open field `kind`s
 * and option sources for `dynamicOptions` names. {@link SettingsFields} renders
 * base kinds natively and dispatches everything else here, so a new domain
 * control is added without touching the kit. Option sources are hooks —
 * {@link SettingsFields} calls every registered source once, unconditionally, to
 * keep hook order stable.
 */
export type WidgetControls = {
  controls: Record<string, React.FC<WidgetControlProps>>;
  optionSources: Record<string, () => WidgetSettingOption[]>;
};

const ControlContext = createContext<WidgetControls>({ controls: {}, optionSources: {} });

export const WidgetControlsProvider = ControlContext.Provider;

export function useWidgetControls(): WidgetControls {
  return useContext(ControlContext);
}
